import AmplitudeUtils from '../../utils/amplitude-utils';
import { getLocalizedString } from '../../utils/i18n-utils';
import BaseForgeCustomizer from '../base-forge';
import {
  forgifyPasswordResetErrorContainer,
  forgifyPasswordResetRequirements
} from '../../utils/password-requirements-customizer';
import {
  getOktaWidgetModel,
  showFormErrors,
  isPasswordLengthValid
} from '../../utils/widget-facade';

export interface PasswordRequirementsSelector {
  headerSelector: string;
  itemListSelector: string;
  containerSelector: string;
}

export default abstract class PasswordResetBaseCustomizer extends BaseForgeCustomizer {
  primaryButtonLabel = getLocalizedString('save.and.continue');

  async shouldCustomize() {
    return this.context.formName === 'reenroll-authenticator-warning' || this.context.controller === 'password-expired';
  }

  protected forgifyController() {
    super.forgifyController();
    this.forgifyPasswordRequirements();
  }
  async customize() {
    AmplitudeUtils.logPasswordResetEvent('Landing', this.getPasswordResetEventProperties());
    await super.customize();
    this.customizePasswordResetButton();
  }

  protected getPasswordResetEventProperties(): Record<string, any> {
    return {};
  }


  protected getPasswordRequirementsSelector(): PasswordRequirementsSelector {
    return {
      containerSelector: '[data-se="password-authenticator--rules"]',
      headerSelector: '.password-authenticator--heading',
      itemListSelector: '.password-authenticator--list',
    };
  }

  protected abstract onPasswordReset(): void

  protected abstract customizePasswordResetButton(): void;

  /**
   * Additional validation hook for subclasses to implement.
   * Return undefined if valid, or an error hash if invalid.
   */
  protected additionalValidation(): Record<string, string> | undefined {
    return undefined;
  }

  /**
   * Wrap Okta's model.validate() with password length validation.
   * Subclasses should call this in their customizePasswordResetButton() implementation.
   */
  protected wrapValidationWithPasswordLengthCheck(): void {
    const model = getOktaWidgetModel();
    const originalValidate = model.validate.bind(model);
    model.validate = () => {
      const response = originalValidate();
      if (response) {
        return response;
      }
      // Check password length
      const isPasswordLengthValidValue = isPasswordLengthValid();
      if (!isPasswordLengthValidValue) {
        showFormErrors({ errorSummary: getLocalizedString('password.length.requirements.not.met') });
        return { 'credentials.passcode': 'error' };
      }
      // Allow subclasses to add additional validation
      return this.additionalValidation();
    };
  }

  /**
   * Override error container to show custom error banner with mapped error messages
   */
  protected forgifyErrorContainer() {
    forgifyPasswordResetErrorContainer(
      this.renderForgeAndHideOktaComponent.bind(this)
    );
  }

  protected forgifyPasswordRequirements() {
    const { containerSelector } = this.getPasswordRequirementsSelector();
    forgifyPasswordResetRequirements(
      containerSelector,
      this.renderReactComponent,
      this.addToHideList.bind(this)
    );
  }

}
